'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { m as motion, LazyMotion, domAnimation } from 'motion/react';
import Image from 'next/image';
import { ResponsiveHeader } from './ResponsiveHeader';
import { ResponsiveFooter } from './ResponsiveFooter';
import { Palette, CheckCircle2, Globe, ChevronDown, Code2, PhoneCall } from 'lucide-react';
import ProjectEstimator from './ProjectEstimator';

const faqs = [
  {
    question: 'Do you develop apps for both iOS and Android platforms?',
    answer:
      'Yes! We build cross-platform mobile apps using modern frameworks like React Native. This allows us to write a single codebase that runs natively on both Apple iOS and Google Android devices, ensuring a consistent user experience while reducing overall development costs for your Hawaii business.',
  },
  {
    question: 'How long does it take to develop a custom mobile app?',
    answer:
      'Mobile app timelines vary based on specific features and backend complexity. A standard app with user authentication and core business functionality typically takes 3-4 months to design, build, test, and prepare for the app stores. We provide a clear, tailored timeline during our initial consultation.',
  },
  {
    question: 'Will you help publish the app to the Apple App Store and Google Play?',
    answer:
      'Yes, we handle the entire deployment and submission process. Getting approved by the Apple App Store and Google Play Store can be complex, but our team ensures your app meets all strict platform guidelines and technical requirements for a smooth and successful launch.',
  },
  {
    question: 'Can the mobile app integrate with my existing software and APIs?',
    answer:
      'Absolutely. We specialize in connecting mobile apps with existing internal systems, databases, CRM platforms, and third-party APIs. We ensure your new mobile app natively communicates with the software tools you already rely on to run your business.',
  },
  {
    question: 'Why should we choose a local Hawaii app developer?',
    answer:
      'Partnering with Zumify means you get a local, dedicated team that understands the unique operational challenges of island businesses. We provide hands-on collaboration, responsive communication in your timezone, and apps uniquely tailored to serve local customers and tourists effectively.',
  },
];

function FAQItem({
  question,
  answer,
  isOpen,
  onClick,
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
}) {
  return (
    <div className="border-b border-white/10">
      <button
        className="w-full py-6 flex items-center justify-between text-left hover:text-purple-400 transition-colors group"
        onClick={onClick}
      >
        <span className="text-xl sm:text-2xl font-semibold text-white group-hover:text-purple-300 transition-colors">
          {question}
        </span>
        <ChevronDown
          className={`w-6 h-6 text-purple-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="overflow-hidden"
      >
        <p className="pb-6 text-stone-400 text-lg leading-relaxed">{answer}</p>
      </motion.div>
    </div>
  );
}

export function ResponsiveMobileApps() {
  const router = useRouter();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [isEstimatorOpen, setIsEstimatorOpen] = useState(false);

  // Reusing established homepage animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0 },
  };

  const fadeInLeft = {
    hidden: { opacity: 0, x: -60 },
    visible: { opacity: 1, x: 0 },
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: 60 },
    visible: { opacity: 1, x: 0 },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const pulseSlow = {
    animate: {
      opacity: [0.3, 0.5, 0.3],
      scale: [1, 1.1, 1],
      transition: { duration: 4, repeat: Infinity, ease: 'easeInOut' as const },
    },
  };

  return (
    <LazyMotion features={domAnimation}>
      <div className="min-h-screen bg-zinc-900 flex flex-col relative overflow-hidden">
        <ResponsiveHeader />

        {/* Hero / Information Section */}
        <section className="relative min-h-[60vh] flex items-center pt-32 lg:pt-56 pb-20">
          {/* Background Image Elements */}
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-zinc-900/80 sm:bg-zinc-900/70 z-10" />{' '}
            {/* Dark Overlay for text readability */}
            {/* Gradient mask to fade bottom edge into the next section */}
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-zinc-900 to-transparent z-10" />
            <img
              src="/images/54ce6a9ef75d51cfa96aaa78e672f88218f8fbc8-768w.webp"
              alt="Responsive Web Design across Devices"
              className="w-full h-full object-cover object-center sm:object-top opacity-50"
            />
          </div>

          {/* Animated Glow Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
            <motion.div
              className="absolute top-20 left-10 w-72 h-72 bg-purple-600/30 rounded-full blur-[100px]"
              variants={pulseSlow}
              animate="animate"
            />
            <motion.div
              className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-600/30 rounded-full blur-[100px]"
              animate={{
                opacity: [0.2, 0.4, 0.2],
                scale: [1, 1.2, 1],
                transition: { duration: 6, repeat: Infinity, ease: 'easeInOut' },
              }}
            />
          </div>

          <div className="relative w-full px-4 sm:px-6 lg:px-8 flex flex-col items-center text-center z-10">
            <div className="max-w-4xl mx-auto backdrop-blur-sm sm:backdrop-blur-none bg-zinc-900/40 sm:bg-transparent p-6 rounded-3xl">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={staggerContainer}
                className="space-y-8"
              >
                <motion.h1
                  variants={fadeInUp}
                  className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight leading-[1.1] drop-shadow-2xl"
                >
                  Mobile App Development <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 drop-shadow-md">
                    In Hawaii
                  </span>
                </motion.h1>

                <motion.p
                  variants={fadeInUp}
                  className="text-lg sm:text-lg md:text-xl text-stone-200 lg:text-stone-300 max-w-2xl mx-auto leading-relaxed drop-shadow-xl font-medium"
                >
                  Looking for a trusted mobile app developer in Hawaii? Zumify designs and builds
                  custom mobile apps for businesses across Oahu, Maui, Kauai, and the Big Island.
                  From concept to launch, we create powerful, user-friendly apps for both iOS and
                  Android that help Hawaii businesses connect with customers, streamline operations,
                  and grow in the digital age.
                </motion.p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Product Information / Impact Section */}
        <section className="px-4 sm:px-6 lg:px-8 pb-20 pt-10 lg:pt-20 relative">
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              className="text-center mb-16 lg:mb-20"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
              variants={fadeInUp}
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                Our Mobile App Services
              </h2>
              <div className="h-1 w-24 bg-gradient-to-r from-purple-500 to-pink-500 mx-auto rounded-full" />
              <p className="mt-6 text-lg text-stone-300 max-w-2xl mx-auto">
                Zumify offers end-to-end mobile app development services for Hawaii businesses of
                all sizes. We handle everything from the initial design concept to the final
                submission on the App Store and Google Play, so you can focus on running your
                business.
              </p>
            </motion.div>

            {/* Solutions & Capabilities Section - Picture + Text */}
            <section className="px-4 sm:px-6 lg:px-8 py-20 lg:py-32 relative overflow-hidden">
              <div className="max-w-7xl mx-auto relative z-10">
                <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
                  {/* Text Side */}
                  <motion.div
                    className="w-full lg:w-1/2 space-y-8"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={fadeInLeft}
                  >
                    <div className="space-y-4">
                      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
                        Why Choose Zumify
                        <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-400">
                          For Mobile App Design in Hawaii
                        </span>
                      </h2>
                      <div className="h-1 w-24 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-full" />
                    </div>

                    <p className="text-lg text-stone-300 leading-relaxed">
                      Hawaii businesses deserve more than just vibe coded apps. Zumify combines the
                      technical expertise of a full-stack development team with a designer's eye for
                      detail and a marketer's understanding of what drives perfect UI/UX experience.
                      We're not just building mobile apps, we're building growth engines for Hawaii
                      businesses.
                    </p>

                    <ul className="space-y-4">
                      {[
                        'Unique & Eye Catching Design',
                        'Works on both iOS and Android',
                        'Use of gestures for unique user experience',
                        'Authentication and user management',
                      ].map((item, idx) => (
                        <motion.li
                          key={idx}
                          className="flex items-center gap-3 text-stone-200"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.2 + idx * 0.1 }}
                        >
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center border border-cyan-500/30">
                            <CheckCircle2 className="w-4 h-4 text-cyan-400" />
                          </div>
                          <span className="text-lg">{item}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </motion.div>

                  {/* Picture Side */}
                  <motion.div
                    className="w-full lg:w-1/2 flex justify-center lg:justify-end"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={fadeInRight}
                  >
                    <div className="relative w-full max-w-[320px] sm:max-w-[360px] lg:max-w-[420px] aspect-[9/16] rounded-3xl bg-zinc-900 border border-white/10 shadow-2xl shadow-purple-500/20 overflow-hidden group">
                      {/* Decorative background glow */}
                      <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-cyan-500/10 pointer-events-none" />

                      {/* Video Showcase */}
                      <div className="absolute inset-0 p-2 sm:p-3 lg:p-4 flex items-center justify-center pointer-events-none">
                        <div className="w-full h-full rounded-2xl md:rounded-[20px] bg-zinc-800/50 backdrop-blur-xl border border-white/10 overflow-hidden shadow-2xl relative group-hover:scale-[1.02] transition-transform duration-700 pointer-events-auto">
                          <video
                            src="/videos/pairing_Spibe_demo.mp4"
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="w-full h-full object-cover bg-zinc-900 rounded-xl md:rounded-[18px]"
                          />
                          {/* Subtle gradient overlay to blend corners nicely */}
                          <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/40 via-transparent to-transparent pointer-events-none rounded-xl md:rounded-[18px]" />
                        </div>
                      </div>

                      {/* Top-right corner accent */}
                      <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/10 blur-3xl pointer-events-none" />
                      {/* Bottom-left corner accent */}
                      <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-500/10 blur-3xl pointer-events-none" />
                    </div>
                  </motion.div>
                </div>
              </div>
            </section>
            {/* 4-Step Process Section - Scrolling Banners */}
            <section className="px-4 sm:px-6 lg:px-8 py-20 lg:py-32 bg-zinc-800/30 overflow-hidden relative border-y border-white/5">
              <div className="max-w-7xl mx-auto relative z-10">
                <motion.div
                  className="text-center mb-16 lg:mb-24"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.5 }}
                  variants={fadeInUp}
                >
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                    Our Mobile App Design{' '}
                    <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                      Process
                    </span>
                  </h2>
                  <div className="h-1 w-24 bg-gradient-to-r from-cyan-500 to-purple-500 mx-auto rounded-full" />
                </motion.div>

                <div className="space-y-24 lg:space-y-40">
                  {[
                    {
                      step: '01',
                      icon: <PhoneCall className="w-10 h-10 text-blue-400" />,
                      title: 'Intro Call and Meeting',
                      description:
                        "We start by learning about your business, your goals, and your target audience. During our introductory call or meeting, we'll discuss your vision for the mobile app and outline a clear plan of action to ensure the final product perfectly aligns with your needs.",
                      color: 'blue',
                      bgColor: 'from-blue-500 to-transparent',
                      borderColor: 'border-blue-500/30',
                      shadow: 'shadow-[0_0_30px_rgba(59,130,246,0.3)]',
                      image: '/images/intro-meeting.webp',
                    },
                    {
                      step: '02',
                      icon: <Palette className="w-10 h-10 text-cyan-400" />,
                      title: 'Figma Design & Layout',
                      description:
                        "Before we write a single line of code, we design your mobile app in Figma: a professional design tool that lets us create high-fidelity mockups of every screen. You'll be able to see exactly how your mobile app will look, review it, request changes, and approve the design before development begins. This step ensures we're aligned on the vision and eliminates costly revisions down the road.",
                      color: 'cyan',
                      bgColor: 'from-cyan-500 to-transparent',
                      borderColor: 'border-cyan-500/30',
                      shadow: 'shadow-[0_0_30px_rgba(34,211,238,0.3)]',
                      image: '/images/prototypeDesign-640w.webp',
                    },
                    {
                      step: '03',
                      icon: <Code2 className="w-10 h-10 text-purple-400" />,
                      title: 'Development Process',
                      description:
                        "Once the design is approved, our developers bring it to life using React Native — a leading cross-platform mobile framework that allows us to build one app that runs natively on both iOS and Android. React Native delivers the performance of a native app with the efficiency of a shared codebase, meaning your app launches faster and costs less to maintain. Alongside the app, we implement a database tailored to your needs to securely store and manage all of your app's data, whether that's user accounts, orders, content, bookings, or more.",
                      color: 'purple',
                      bgColor: 'from-purple-500 to-transparent',
                      borderColor: 'border-purple-500/30',
                      shadow: 'shadow-[0_0_30px_rgba(168,85,247,0.3)]',
                      image: '/images/development.webp',
                    },
                    {
                      step: '04',
                      icon: <Globe className="w-10 h-10 text-pink-400" />,
                      title: 'Launching it on App Store/Google Play Store',
                      description:
                        "When development is complete, we use Expo to streamline the build and deployment process. Expo is a powerful toolchain built around React Native that simplifies submitting your app to both the Apple App Store and Google Play Store. We handle the full deployment pipeline, building the app binaries, meeting each platform's requirements, and submitting for review, so your mobile app goes live on both platforms smoothly and professionally.",
                      color: 'pink',
                      bgColor: 'from-pink-500 to-transparent',
                      borderColor: 'border-pink-500/30',
                      shadow: 'shadow-[0_0_30px_rgba(236,72,153,0.3)]',
                      image: '/images/SpibeSparks.webp',
                    },
                  ].map((step, idx) => {
                    const isEven = idx % 2 === 1;

                    return (
                      <div
                        key={idx}
                        className="relative z-10 flex flex-col lg:flex-row items-center gap-12 lg:gap-20"
                      >
                        {/* Text Side */}
                        <motion.div
                          className={`w-full lg:w-1/2 space-y-6 ${isEven ? 'lg:order-2' : 'lg:order-1'}`}
                          initial="hidden"
                          whileInView="visible"
                          viewport={{ once: true, amount: 0.3 }}
                          variants={isEven ? fadeInRight : fadeInLeft}
                        >
                          <div className="flex items-center gap-6 mb-2">
                            <span
                              className={`text-6xl lg:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-br ${step.bgColor} opacity-50`}
                            >
                              {step.step}
                            </span>
                            <div
                              className={`p-4 rounded-2xl bg-zinc-800 border ${step.borderColor} shadow-lg`}
                            >
                              {step.icon}
                            </div>
                          </div>

                          <h3 className="text-3xl lg:text-4xl font-bold text-white leading-tight">
                            {step.title}
                          </h3>

                          <p className="text-lg text-stone-300 leading-relaxed max-w-xl">
                            {step.description}
                          </p>
                        </motion.div>

                        {/* Image / Visual Side */}
                        <motion.div
                          className={`w-full lg:w-1/2 ${isEven ? 'lg:order-1' : 'lg:order-2'}`}
                          initial="hidden"
                          whileInView="visible"
                          viewport={{ once: true, amount: 0.3 }}
                          variants={isEven ? fadeInLeft : fadeInRight}
                        >
                          <div
                            className={`relative w-full aspect-[4/3] rounded-[24px] bg-zinc-900 border-2 ${step.borderColor} ${step.shadow} overflow-hidden group`}
                          >
                            {'image' in step && step.image ? (
                              <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 to-zinc-900 overflow-hidden flex items-center justify-center p-6">
                                <img
                                  src={step.image}
                                  alt={step.title}
                                  className="w-full h-full object-contain rounded-xl group-hover:scale-105 transition-transform duration-700 relative z-10 shadow-xl shadow-black/50"
                                />
                              </div>
                            ) : (
                              <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center p-8">
                                <div
                                  className={`w-full h-full border border-white/5 rounded-2xl bg-gradient-to-br ${step.bgColor} backdrop-blur-3xl flex items-center justify-center group-hover:scale-105 transition-transform duration-700`}
                                >
                                  {/* Decorative element acting as an image placeholder */}
                                  <div className="w-32 h-32 rounded-full bg-white/5 backdrop-blur-md flex items-center justify-center border border-white/10">
                                    {step.icon}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Hover Overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                          </div>
                        </motion.div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>

            <div className="max-w-4xl mx-auto space-y-12 lg:space-y-16 mt-16">
              {/* Text Block 1 */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInUp}
                className="bg-zinc-900/40 p-8 lg:p-10 rounded-3xl border border-white/5 backdrop-blur-sm relative overflow-hidden group"
              >
                <div className="absolute top-0 left-0 w-1 p-0 h-full bg-gradient-to-b from-cyan-400 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-300" />
                <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4 pl-4">
                  Custom iOS & Android App Development
                </h3>
                <p className="text-lg text-stone-300 leading-relaxed pl-4">
                  We build apps that work beautifully on both iPhone and Android devices using a
                  single, unified codebase. That means faster development, lower costs, and a
                  consistent experience for all of your users. Every app we build is tailored to
                  your brand, your users, and your specific goals.
                </p>
              </motion.div>

              {/* Text Block 2 */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInLeft}
                className="bg-zinc-900/40 p-8 lg:p-10 rounded-3xl border border-white/5 backdrop-blur-sm relative overflow-hidden group"
              >
                <div className="absolute top-0 left-0 w-1 pt-8 h-full bg-gradient-to-b from-purple-400 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-300" />
                <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4 pl-4">
                  User-Centered UI/UX Design
                </h3>
                <p className="text-lg text-stone-300 leading-relaxed pl-4">
                  A great app isn't just functional, it's intuitive and enjoyable to use. We design
                  every screen with your end user in mind, creating interfaces that are clean, easy
                  to navigate, and visually aligned with your brand. Good design reduces friction,
                  increases engagement, and keeps users coming back.
                </p>
              </motion.div>

              {/* Text Block 3 */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInRight}
                className="bg-zinc-900/40 p-8 lg:p-10 rounded-3xl border border-white/5 backdrop-blur-sm relative overflow-hidden group"
              >
                <div className="absolute top-0 left-0 w-1 pt-8 h-full bg-gradient-to-b from-pink-400 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-300" />
                <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4 pl-4">
                  Scalable Backend & Database Integration
                </h3>
                <p className="text-lg text-stone-300 leading-relaxed pl-4">
                  Your app needs a reliable foundation to store and manage data. We architect and
                  integrate robust databases and backend systems that scale with your business.
                  Whether your app needs user profiles, real-time data, product catalogs,
                  appointment records, or anything in between, we build the infrastructure to
                  support it securely and efficiently.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 lg:py-24 bg-zinc-900/50 relative z-20">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                Answers to Your Questions About Mobile Apps
              </h2>
              <div className="h-1 w-24 bg-gradient-to-r from-purple-500 to-cyan-500 mx-auto rounded-full" />
            </motion.div>

            <div className="space-y-2">
              {faqs.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openIndex === index}
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Mobile App Pricing Tiers Section */}
        <section className="px-4 sm:px-6 lg:px-8 py-20 bg-zinc-900/50 relative z-20">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                Mobile App Pricing{' '}
                <span className="bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
                  Tiers
                </span>
              </h2>
              <p className="text-lg sm:text-xl text-stone-300 max-w-3xl mx-auto">
                Explore our Price Tiers of the products we craft for Hawaii's businesses.
                <br className="hidden sm:block" />
                <span className="text-stone-400 text-sm sm:text-base mt-2 block">
                  Note: All quotes will be finalized based on complexity, time, and landscape of
                  project proposal. Minimums enforced.
                </span>
              </p>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mt-8 flex justify-center"
              >
                <button
                  onClick={() => setIsEstimatorOpen(true)}
                  className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-white font-bold py-3 px-8 rounded-full shadow-[0_0_20px_rgba(6,182,212,0.3)] hover:shadow-[0_0_30px_rgba(6,182,212,0.5)] transition-all transform hover:scale-105 flex items-center gap-2"
                >
                  Try Our Project Estimator
                </button>
              </motion.div>
            </motion.div>

            <div className="space-y-12">
              {/* Mobile Apps Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="border border-rose-500/30 rounded-2xl p-4 sm:p-6 bg-zinc-900/50"
              >
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Left Panel */}
                  <div className="lg:w-1/4 bg-black rounded-xl p-6 flex flex-col justify-end min-h-[300px] relative overflow-hidden border border-white/5 group">
                    {/* Poster Background */}
                    <Image
                      src="/images/SpibeSparks.webp"
                      alt="Mobile Apps Tier Poster"
                      fill
                      className="object-cover opacity-60 group-hover:opacity-80 transition-opacity duration-500"
                    />
                    {/* Decorative Background for text readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none" />
                    <div className="absolute inset-0 opacity-40 bg-gradient-to-b from-rose-500/20 to-transparent pointer-events-none mix-blend-overlay" />

                    <div className="relative z-10 mt-auto">
                      <h3 className="text-2xl font-bold text-white mb-2">Mobile Apps</h3>
                      <div className="space-y-1">
                        <p className="text-sm text-stone-400">Mobile Launch</p>
                        <p className="text-sm text-stone-400">Mobile Growth</p>
                        <p className="text-sm text-stone-400">Mobile Enterprise</p>
                      </div>
                    </div>
                  </div>

                  {/* Right Panel - Tiers */}
                  <div className="lg:w-3/4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Tier 1 */}
                    <div className="bg-zinc-800/50 rounded-xl p-6 border border-cyan-500/20 flex flex-col">
                      <h4 className="text-xl font-semibold text-cyan-400 mb-4 text-center">Mobile Launch</h4>
                      <ul className="space-y-3 mb-8 flex-1">
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-cyan-500">•</span>
                          <span>
                            Custom-designed mobile experience (IOS build + Android build)
                          </span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-cyan-500">•</span>
                          <span>3-6 core screens focused on the single main use case</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-cyan-500">•</span>
                          <span>Basic frontend + lightweight backend (just enough to function)</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-cyan-500">•</span>
                          <span>Simple data handling (basic storage or minimal database)</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-cyan-500">•</span>
                          <span>Core feature only (no extras, no complexity)</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-cyan-500">•</span>
                          <span>Built for iteration (clean, scalable starting structure)</span>
                        </li>
                      </ul>
                      <div className="text-center pt-4 border-t border-white/10 space-y-4">
                        <div>
                          <p className="text-sm text-stone-400 mb-1">Flexible Development Pricing</p>
                          <p className="text-xl font-bold text-cyan-400">$90 / hr</p>
                          <div className="mt-2 space-y-1">
                            <p className="text-s text-stone-400">Average Estimated Hours: <span className="text-stone-300">35-55</span></p>
                            <p className="text-s text-stone-400">Estimated Investment: <br/> <span className="text-cyan-400">$3,100–$4,950</span></p>
                          </div>
                        </div>
                        <div className="pt-4 border-t border-white/10">
                          <p className="text-sm text-stone-400 mb-1">Maintenance</p>
                          <p className="text-lg font-bold text-white">Starter Plan</p>
                          <p className="text-cyan-400">$299.99 / Month</p>
                        </div>
                      </div>
                    </div>

                    {/* Tier 2 */}
                    <div className="bg-zinc-800/50 rounded-xl p-6 border border-purple-500/20 flex flex-col">
                      <h4 className="text-xl font-semibold text-purple-400 mb-4 text-center">
                        Mobile Growth
                      </h4>
                      <ul className="space-y-3 mb-8 flex-1">
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-purple-500">•</span>
                          <span>Full frontend + backend architecture</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-purple-500">•</span>
                          <span>Scalable updates and maintenance </span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-purple-500">•</span>
                          <span>Complete UX/UI polish (transitions, states, error handling)</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-purple-500">•</span>
                          <span>Structured database implementation</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-purple-500">•</span>
                          <span>Core integrations (auth, basic APIs, notifications if needed)</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-purple-500">•</span>
                          <span>Scalable infrastructure (handles real users)</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-purple-500">•</span>
                          <span>Unlimited feature requests (1 active at a time)</span>
                        </li>
                      </ul>
                      <div className="text-center pt-4 border-t border-white/10 space-y-4">
                        <div>
                          <p className="text-sm text-stone-400 mb-1">Flexible Development Pricing</p>
                          <p className="text-xl font-bold text-purple-400">$110 / hr</p>
                          <div className="mt-2 space-y-1">
                            <p className="text-s text-stone-400">Average Estimated Hours: <span className="text-stone-300">60-100</span></p>
                            <p className="text-s text-stone-400">Estimated Investment: <br/> <span className="text-purple-400">$6,600-$11,000</span></p>
                          </div>
                        </div>
                        <div className="pt-4 border-t border-white/10">
                          <p className="text-sm text-stone-400 mb-1">Maintenance</p>
                          <p className="text-lg font-bold text-white">Growth Plan</p>
                          <p className="text-purple-400">$1,499.99 / month</p>
                        </div>
                      </div>
                    </div>

                    {/* Tier 3 */}
                    <div className="bg-zinc-800/50 rounded-xl p-6 border border-pink-500/20 flex flex-col">
                      <h4 className="text-xl font-semibold text-pink-400 mb-4 text-center">
                        Mobile Enterprise
                      </h4>
                      <ul className="space-y-3 mb-8 flex-1">
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-pink-500">•</span>
                          <span className="block">
                            Advanced systems:
                            <br />
                            - User authentication flows (multi-role, permissions)
                            <br />
                            - Payments / subscriptions
                            <br />
                            - Real-time features (chat, updates, syncing)
                            <br />- Dashboards (admin + user)
                          </span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-pink-500">•</span>
                          <span>Complex API integrations & third-party services</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-pink-500">•</span>
                          <span>Performance optimization (speed, load handling, responsiveness)</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-pink-500">•</span>
                          <span>Security hardening (data protection, auth layers)</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-pink-500">•</span>
                          <span>Scalable backend architecture (cloud infra, load ready systems)</span>
                        </li>
                        <li className="text-sm text-stone-300 flex items-start">
                          <span className="mr-2 text-pink-500">•</span>
                          <span>Ongoing development + growth support</span>
                        </li>
                      </ul>
                      <div className="text-center pt-4 border-t border-white/10 space-y-4">
                        <div>
                          <p className="text-sm text-stone-400 mb-1">Flexible Development Pricing</p>
                          <p className="text-xl font-bold text-pink-400">$160 / hr</p>
                          <div className="mt-2 space-y-1">
                            <p className="text-s text-stone-400">Average Estimated Hours: <span className="text-stone-300">250+</span></p>
                            <p className="text-s text-stone-400">Estimated Investment: <br/> <span className="text-pink-400">$40,000+</span></p>
                          </div>
                        </div>
                        <div className="pt-4 border-t border-white/10">
                          <p className="text-sm text-stone-400 mb-1">Maintenance</p>
                          <p className="text-lg font-bold text-white">Premium Plan</p>
                          <p className="text-pink-400">$2,999.99 / month</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Project Estimator Modal */}
          <ProjectEstimator isOpen={isEstimatorOpen} onClose={() => setIsEstimatorOpen(false)} />
        </section>

        {/* CTA Bottom Section */}
        <motion.section
          className="px-4 sm:px-6 lg:px-8 py-20 mb-10"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
          variants={fadeInUp}
        >
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-cyan-500/10 via-purple-500/10 to-pink-500/10 backdrop-blur-xl border border-white/10 rounded-3xl p-8 lg:p-12 shadow-[0_0_50px_rgba(168,85,247,0.15)]">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready for a new Mobile App?
            </h2>
            <p className="text-lg sm:text-xl text-stone-300 mb-8 max-w-2xl mx-auto">
              Schedule a free consultation call and let's craft a digital experience that elevates
              your brand.
            </p>
            <button
              onClick={() => router.push('/contact')}
              className="px-10 py-4 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 text-white rounded-full font-bold text-lg hover:scale-105 transition-transform duration-300 shadow-lg shadow-purple-500/30"
            >
              Get Started Today
            </button>
          </div>
        </motion.section>

        <ResponsiveFooter />
      </div>
    </LazyMotion>
  );
}
